import { z } from 'zod';
import { nullableUuidString, optionalPresentUuidString, requireAtLeastOneField, } from '../../zod-helpers/index.js';
import { manifestNumberSchema, updateSalesOrderDeliveryFieldsShape, } from '../delivery-fields/index.js';
import { SALES_ORDER_HEADER_CONSTRAINTS } from '../header-constraints.js';
import { salesOrderAdjustmentLinesSchema } from './sales-order-adjustment-lines-schema.js';
import { salesOrderExpectedAdjustmentsSchema } from './sales-order-expected-adjustments-schema.js';
// Partial patch: omitted leaves the value, null clears a nullable field, and .strict() rejects unknown keys.
export const updateSalesOrderInputSchema = requireAtLeastOneField(
// Carries only the fields updateSalesOrder owns, since campaign attribution has its own mutations.
z
    .object({
    // NOT NULL column: present-if-touched, never cleared; a change triggers term re-pricing.
    paymentTermsId: optionalPresentUuidString('Invalid payment terms selection.').optional(),
    // NOT NULL column (sales_order.salesman_id): same present-if-touched contract as paymentTermsId.
    salesmanId: optionalPresentUuidString('Invalid salesperson selection.').optional(),
    // Carrier, carrier service, ship terms, FOB point, priority and ship date (NUDOOL-113).
    ...updateSalesOrderDeliveryFieldsShape,
    // custom_fields.customer_contact_id (via mergeBookSalesOrderCustomFields), nullable.
    customerContactId: nullableUuidString('Invalid customer contact selection.').optional(),
    // Selection-only and never cleared, since ship_to_state_id backs the brand-scope filter and nulling it would hide the order.
    shipToAddressId: optionalPresentUuidString('Invalid ship-to address selection.').optional(),
    // NOT NULL column: a change moves the order's reservations and open pick to that warehouse.
    locationGroupId: optionalPresentUuidString('Invalid warehouse selection.').optional(),
    // Selection-only like shipToAddressId; a saved Billing address replaces the bill-to snapshot (E-4).
    billToAddressId: optionalPresentUuidString('Invalid bill-to address selection.').optional(),
    customerPurchaseOrder: z
        .string()
        .max(SALES_ORDER_HEADER_CONSTRAINTS.customerPurchaseOrder.maxLength, `${SALES_ORDER_HEADER_CONSTRAINTS.customerPurchaseOrder.label} must be ${SALES_ORDER_HEADER_CONSTRAINTS.customerPurchaseOrder.maxLength} characters or less.`)
        .optional()
        .nullable(),
    // Customer-facing note (sales_order.note).
    note: z
        .string()
        .max(SALES_ORDER_HEADER_CONSTRAINTS.note.max, `${SALES_ORDER_HEADER_CONSTRAINTS.note.label} must be ${SALES_ORDER_HEADER_CONSTRAINTS.note.max.toLocaleString('en-US')} characters or less.`)
        .optional()
        .nullable(),
    // custom_fields.internal_notes (via mergeBookSalesOrderCustomFields).
    internalNotes: z
        .string()
        .max(SALES_ORDER_HEADER_CONSTRAINTS.internalNotes.max, `${SALES_ORDER_HEADER_CONSTRAINTS.internalNotes.label} must be ${SALES_ORDER_HEADER_CONSTRAINTS.internalNotes.max.toLocaleString('en-US')} characters or less.`)
        .optional()
        .nullable(),
    // custom_fields.delivery_preferences (via mergeBookSalesOrderCustomFields).
    deliveryPreferences: z
        .string()
        .max(SALES_ORDER_HEADER_CONSTRAINTS.deliveryPreferences.max, `${SALES_ORDER_HEADER_CONSTRAINTS.deliveryPreferences.label} must be ${SALES_ORDER_HEADER_CONSTRAINTS.deliveryPreferences.max.toLocaleString('en-US')} characters or less.`)
        .optional()
        .nullable(),
    manifestNumber: manifestNumberSchema,
    // The complete desired set: omitted leaves lines untouched, an empty array removes all, and a missing kind removes that line.
    adjustments: salesOrderAdjustmentLinesSchema.optional(),
    // Checked against the stored set only beside `adjustments`; null or omitted skips the check.
    expectedAdjustments: salesOrderExpectedAdjustmentsSchema.nullable().optional(),
    // true ignores any TAX entries and applies the brand and ship-to state tax config (D16).
    applyTaxDefaults: z.boolean().optional(),
})
    .strict());
//# sourceMappingURL=update-sales-order-input-schema.js.map